import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeNumber, ThemeMode, ThemeConfig } from '../types';

interface ThemeContextType {
  theme: ThemeNumber;
  mode: ThemeMode;
  setTheme: (theme: ThemeNumber) => void;
  toggleMode: () => void;
  themeConfig: ThemeConfig;
}

const themeConfigs: Record<ThemeNumber, ThemeConfig> = {
  0: {
    number: 0,
    name: 'Toonami Classic',
    description: 'TOM 3.0 era, the Absolution, space station aesthetic',
    font: 'Orbitron',
    vibe: 'futuristic'
  },
  1: {
    number: 1,
    name: 'Vaporwave Aesthetic',
    description: '90s nostalgia, Japanese text overlays, neon grids',
    font: 'VT323',
    vibe: 'retro'
  },
  2: {
    number: 2,
    name: 'Retro Anime',
    description: 'Cowboy Bebop, scanline effects, VHS static',
    font: 'Comic Sans MS',
    vibe: 'vintage'
  },
  3: {
    number: 3,
    name: 'Matrix Cyberpunk',
    description: 'Falling code, cyber dystopia, Ghost in the Shell',
    font: 'Courier New',
    vibe: 'cyber'
  },
  4: {
    number: 4,
    name: 'Pastel Kawaii',
    description: 'Moe overload, cute AF, sparkles everywhere',
    font: 'Fredoka One',
    vibe: 'cute'
  }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'weebcoders-theme';
const MODE_STORAGE_KEY = 'weebcoders-mode';
const THEME_ACTIVATION_COUNT = 5;

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Load saved theme or default to 0
  const [theme, setThemeState] = useState<ThemeNumber>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved ? (parseInt(saved) as ThemeNumber) : 0;
  });

  // Load saved mode or detect system preference
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(MODE_STORAGE_KEY);
    if (saved) return saved as ThemeMode;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Theme switching easter egg: Press same number key 5 times
  const [keyPressCount, setKeyPressCount] = useState(0);
  const [lastKey, setLastKey] = useState<string>('');

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only listen for number keys 0-4
      if (e.key >= '0' && e.key <= '4') {
        if (e.key === lastKey) {
          const newCount = keyPressCount + 1;
          if (newCount === THEME_ACTIVATION_COUNT) {
            const newTheme = parseInt(e.key) as ThemeNumber;
            setTheme(newTheme);
            console.log(`%c🎨 Theme activated: ${themeConfigs[newTheme].name}`, 'font-size: 16px; color: var(--color-primary)');
            setKeyPressCount(0);
          } else {
            setKeyPressCount(newCount);
          }
        } else {
          setLastKey(e.key);
          setKeyPressCount(1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [keyPressCount, lastKey]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme.toString());
    root.classList.toggle('light', mode === 'light');
    root.classList.toggle('dark', mode === 'dark');
    localStorage.setItem(THEME_STORAGE_KEY, theme.toString());
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [theme, mode]);

  const setTheme = (newTheme: ThemeNumber) => {
    setThemeState(newTheme);
  };

  const toggleMode = () => {
    setModeState(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      mode,
      setTheme,
      toggleMode,
      themeConfig: themeConfigs[theme]
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
